import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "@/services/i18n/client";
import { route, RouteKeys } from "@/services/routing/routes";
import { RoleNavigation } from "@/features/auth/components/role-navigation";
import { UserMenu } from "@/features/auth/components/user-menu";
import { MobileNav } from "./mobile-nav";
import { LanguageSwitcher } from "./language-switcher";
import { HelpCircle, LifeBuoy } from "lucide-react";
import { useState } from "react";
import { Button } from "@heycircle/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@heycircle/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@heycircle/ui/sheet";
import HelpPanelContent from "@/features/help/components/help-panel-content";

function HelpMenuButton() {
  const { t } = useTranslation("help");
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("openHelp")}>
          <HelpCircle className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full overflow-hidden sm:max-w-md lg:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t("title")}</SheetTitle>
          <SheetDescription className="sr-only">{t("subtitle")}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 h-[calc(100vh-7rem)]">{open && <HelpPanelContent />}</div>
      </SheetContent>
    </Sheet>
  );
}

function SupportMenu() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <LifeBuoy className="h-5 w-5" />
          <span className="sr-only">{t("support.title")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("support.title")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(route(RouteKeys.DEFECT_REPORTING))}>
          {t("support.defectReportingLink")}
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`mailto:${t("support.contactEmail")}`}>{t("support.contactUs")}</a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppNavigation() {
  const { t } = useTranslation("common");

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-14 w-full items-center px-4 sm:px-6 lg:px-8">
        {/* Mobile: hamburger menu — hidden on lg+ */}
        <div className="lg:hidden">
          <MobileNav />
        </div>
        <Link
          to={route(RouteKeys.DASHBOARD)}
          className="ml-2 mr-6 flex items-center space-x-2 lg:ml-0"
        >
          <img src="/logo/logo_black.svg" alt={t("appName")} className="h-8 w-auto" />
        </Link>
        {/* Desktop: role-based nav links with submenu support — hidden below lg */}
        <div className="hidden lg:flex flex-1">
          <RoleNavigation />
        </div>
        <div className="flex-1 lg:flex-none"></div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <HelpMenuButton />
          <SupportMenu />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
